import React, { useEffect, useState } from 'react';
import ActiveForm from './partials/ActiveForm'
import ValidatedForm from './partials/ValidatedForm';
import UnfilledForm from './partials/UnfilledForm';
import logger from '../lib/logger';
import { Formik, Field, Form, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import useLocalStorage from '../hooks/useLocalStorage';
import integromat from '../lib/integromat';
import getCarEvaluationData from '../lib/getCarEvaluationData';
import { phoneRemovePlus, phoneTransform, yupEmail, yupPhone } from '../lib/yupShared';


const EstimateForm = props => {
    const {
        formValues,
        view,
        isStepValidated,
        handleFormUpdates,
        handleSteps, 
        setStep,
        checkNoCarsEvaluated,
        handleCarsEvaluated
    } = props;

    const [carEstimate, setCarEstimate] = useState({});
    const [renderView, setRenderView] = useState(view);
    const [sendDataStatus, setSendDataStatus] = useState(false);

    const ninetyDaysInMilliseconds = 7776000000;
    const [storedEstimate, setStoredEstimate] = useLocalStorage('evaluate-car-estimate', null, ninetyDaysInMilliseconds);

    // Define the time frame options in an array
    const sellTimeFrameOptions = [
        { value: '3_months', label: 'Inom 3 månader' },
        { value: '4_6_months', label: 'Inom 4-6 månader' },
        { value: '7_12_months', label: 'Inom 7-12 månader' },
        { value: 'not_selling', label: 'Jag vill inte sälja min bil' }
    ];

    const buyCarOptions = [
        { value: 'yes', label: 'Ja' },
        { value: 'no', label: 'Nej' }
    ];



    // Reset all values
    const newCar = e => {
        e.preventDefault();
        props.clearFormValues();
    }

    const nextStep = e => {
        e.preventDefault();
        setStep(4);
    }
    // Default content 
    const content = {
        heading: 'Uppskattad värdering',
        desc: 'Fyll i din e-postadress och ta del av din värdering direkt. Vi skickar även en kopia på mail som du kan spara om du vill.',
        message: ''
    }


    // Fake loader before email screen
    useEffect(() => {
        let renderTimeout;
        if (view === 'active' && !isStepValidated(3)) {
            setRenderView('loading');
            renderTimeout = setTimeout(function () {
                setRenderView(view);
            }, process.env.REACT_APP_LOADING_TIMEOUT)   //9100    
        } else {
            setRenderView(view);
        }
        return () => {
            if (renderTimeout) {
                clearTimeout(renderTimeout)
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [view])

    useEffect(() => {
        logger('renderview:', renderView);
    }, [renderView])


    // Update data but stay on this step
    function handleUpdate(values) {
        logger('handle update');
        // Move to next step
        handleSteps(3, values, 3);
        // Query car estimate from API
        getData();
    }

    /**
     * Checks if car exists in localstorage
     * @returns boolean
     */
    function getLocalStorage() {
        // Check  if data is exists in local storage
        if (storedEstimate?.registrationNumber === formValues.regnr) {
            logger('Loaded data from storedEstimate');
            return storedEstimate
        }

        // Else return false and set estimate to empty object
        return false;
    }

    // Get data from Wayke
    async function getWaData() {

        if( !checkNoCarsEvaluated() ) {
            return false
        }

        const data = await getCarEvaluationData(formValues.regnr, formValues.mileage)

        if (!data) {
            return false;
        }
        logger('Loaded data from getCarEvaluationData');

        window?.dataLayer?.push({
            event: 'valuationInitialized'
        });

        return data;

    }

    /**
     * Load data from LocalStorage or from Wayke
     * 
     */
    async function getData() {
        // Show loading screen
        setSendDataStatus(false);
        setRenderView('loading');

        // Load data from localstorage or make api request
        const data = getLocalStorage() || await getWaData();

        logger(data)

        // if no data found set view and return
        if( !data ) {
            setRenderView('no-data-found');
            setCarEstimate({})
            setStoredEstimate({})   
            handleCarsEvaluated({})
            handleFormUpdates({
                price_filtered: null,
                price_prediction: null,
                make: '',
                model: '',
                color: '',
                modelYear: '',
            });
            return false
        }

        // Update states
        setCarEstimate(data);
        setStoredEstimate(data);
        handleCarsEvaluated(data.registrationNumber);

        handleFormUpdates({
            make: data.dataUsed.manufacturer,
            model: data.dataUsed.modelSeries,
            modelYear: data.dataUsed.modelYear,
            color: data.dataUsed.color,
            price_prediction: data?.price?.prediction || false,
            fuelType: data.dataUsed.fuelType,
            enginePower: data.dataUsed.enginePower,
        });

        // Set view
        setRenderView('hasdata');
        return true
    }

    useEffect(() => {
        logger('useEffect for integromat triggered')
        logger(carEstimate);
        if (
            sendDataStatus === false &&
            formValues.price_filtered !== null &&
            formValues.price_prediction !== null &&
            formValues.price_prediction > 0 &&
            Object.keys(carEstimate).length > 1
        ) {
            logger('useEffect for integromat successful')            
            logger('carestimate', carEstimate);
            setRenderView('hasdata');
            setSendDataStatus(true);

            // Send data to integromat
            if (Object.keys(carEstimate).length > 1) {
                // Translate time frame value to text
                const timeFrameOption = sellTimeFrameOptions.find(opt => opt.value === formValues.sellTimeFrame);
                const timeFrameText = timeFrameOption ? timeFrameOption.label : '';
                

                integromat.estimate({
                    registrationNumber: carEstimate.registrationNumber,
                    mileage: (carEstimate.dataUsed?.km / 10),
                    condition: formValues.condition,
                    sellingPrice: formValues.price_filtered ? formValues.price_filtered : 0,
                    waykeValue: formValues.price_prediction ? formValues.price_prediction : 0,
                    'sellingPrice-fancy': formValues.price_filtered ? formValues.price_filtered?.toLocaleString() + ' kr' : 'Kan ej värderas',
                    'waykeValue-fancy': formValues.price_prediction ? formValues.price_prediction?.toLocaleString() + ' kr' : 'Kan ej värderas',
                    email: formValues.email,
                    phone: phoneRemovePlus(formValues.phone),
                    make: carEstimate.dataUsed.manufacturer,
                    model: carEstimate.dataUsed.modelSeries,
                    modelYear: carEstimate.dataUsed.modelYear,
                    color: carEstimate.dataUsed.color,
                    carAd: 'Nej',
                    newsletterSubscribe: formValues.newsletter,
                    timeFrame: timeFrameText,
                    buyCar: formValues.buyCar,
                    beContactedBySeller: formValues.beContactedBySeller,
                    vinNumber: carEstimate.dataUsed.vinNumber
                });
            }
        }
    }, [formValues, sendDataStatus, carEstimate]);




    /**
     * starts with 0 or +12[34]
     * then 6-9 numbers
     */
    

    switch (renderView) {
        case 'active':

            const form = (
                <>
                    <Formik
                        initialValues={{
                            email: formValues.email,
                            phone: formValues.phone,
                            newsletter: formValues.newsletter,
                            sellTimeFrame: sellTimeFrameOptions[0].value,
                            buyCar: formValues.buyCar,
                            beContactedBySeller: '', // Added initial value
                        }}
                        validationSchema={Yup.object({
                            email: yupEmail,
                            phone: yupPhone
                        })}
                        onSubmit={(values, { setSubmitting }) => {
                            setTimeout(() => {
                                // transform phone number
                                values.phone = phoneTransform(values.phone)

                                console.log({ values });
                                handleUpdate(values);
                                setSubmitting(false);
                            }, 400);
                        }}
                    >
                        {({ isValid, values }) => (
                            <Form>
                                <div className="form-fields estimate">
                                    <div className="input-field">
                                        <label htmlFor="email">Din e-post</label>
                                        <Field
                                            name="email"
                                            type="email"
                                            placeholder="Ange din e-postadress"
                                        />
                                        <div className="form-error-msg">
                                            <ErrorMessage name="email" />
                                        </div>
                                    </div>
                                    <div className="input-field">
                                        <label htmlFor="phone">Ditt telefonnummer</label>
                                        <Field
                                            name="phone"
                                            type="text"
                                            placeholder="Ange ditt telefonnummer"
                                        />
                                        <div className="form-error-msg">
                                            <ErrorMessage name="phone" />
                                        </div>
                                    </div>
                                    <div className="input-field">
                                        <label htmlFor="sellTimeFrame">När har du tänkt sälja din bil?</label>
                                        <Field as="select" name="sellTimeFrame">
                                            {sellTimeFrameOptions.map(option => (
                                                <option key={option.value} value={option.value}>
                                                    {option.label}
                                                </option>
                                            ))}
                                        </Field>
                                        <div className="form-error-msg">
                                            <ErrorMessage name="sellTimeFrame" />
                                        </div>
                                    </div>
                                    <div className="input-field">
                                        <label htmlFor="buyCar">Går du i tankarna på att köpa bil?</label>
                                        <Field as="select" name="buyCar">
                                            <option value="">Välj ett alternativ</option>
                                            {buyCarOptions.map(option => (
                                                <option key={option.value} value={option.value}>
                                                    {option.label}
                                                </option>
                                            ))}
                                        </Field>
                                        <div className="form-error-msg">
                                            <ErrorMessage name="buyCar" />
                                        </div>
                                    </div>
                                    {values.buyCar === 'yes' && (
                                        <div className="input-field">
                                            <label htmlFor="beContactedBySeller">Vill du att en säljare kontaktar dig?</label>
                                            <Field as="select" name="beContactedBySeller">
                                                <option value="">Välj ett alternativ</option>
                                                {buyCarOptions.map(option => (
                                                    <option key={option.value} value={option.value}>
                                                        {option.label}
                                                    </option>
                                                ))}
                                            </Field>
                                            <div className="form-error-msg">
                                                <ErrorMessage name="beContactedBySeller" />
                                            </div>
                                        </div>
                                    )}
                                    <div className="input-field">
                                        <label htmlFor="newsletter">Nyhetsbrev</label>
                                        <Field name="newsletter" type="checkbox" />
                                    </div>
                                </div>

                                <div className="form-button-container">
                                    <button className="form-button-wide" type="submit" disabled={!isValid}>Hämta värdering</button>
                                </div>
                            </Form>
                        )}
                    </Formik>
                </>
            )
            return (<ActiveForm content={content} form={form} step={3} handleUpdate={handleUpdate} />)
        case 'unfilled': return (<UnfilledForm content={content} step={3} />)
        case 'validated':
            return (<ValidatedForm content={content} step={3} values={[formValues.price_filtered?.toLocaleString() + ' kr']} setStep={props.setStep} />)
        case 'loading': return (
            <>
                <div className="active-form">
                    <div className="text-step">Steg 3 av 4</div>
                    <div className="text-title">{content.heading}</div>
                    <div className="field-is-loading">
                        <div className="loader-bar"></div>
                        <p>Hämtar data för bilvärdering. Tack för att du väntar.</p>
                    </div>
                </div>
            </>
        )
        case 'hasdata':


            return (
                <>
                    <div className="active-form">
                        <div className="text-step">Steg 3 av 4</div>
                        <div className="text-title">{content.heading}</div>
                        <div className="validated-values">
                            <span className="checkmark">✓ </span><span >{formValues.email}</span>
                        </div>
                        <div className="form-fields">
                            {formValues.price_filtered
                                ? <ShowCarValue price_filtered={formValues.price_filtered} />
                                : <ShowCarNonValueble />
                            }
                        </div >
                        <div className="form-button-container">
                            <button className="form-button-wide red" onClick={nextStep}>Gå vidare</button>
                        </div>
                        <div className="field-link">
                            <button className="form-button-link" onClick={newCar}>
                                Värdera en annan bil
                            </button>
                        </div>

                    </div >
                </>
            )
        case 'no-data-found': return (
            <>
                <div className="active-form">
                    <div className="text-step">Steg 3 av 4</div>
                    <div className="text-title">{content.heading}</div>
                    <div className="validated-values">
                        <span className="checkmark">✓ </span><span >{formValues.email}</span>
                    </div>

                    <div className="info-sign error">
                        <p>Ingen data hittad eller så har du gjort för många sökningar. Vänligen prova igen om en stund.</p>
                    </div>

                    <div className="text-fineprint">Tjänsten är begränsad till ett visst antal sökningar under en begränsad tid. Om du har gjort många sökningar kan du behöva vänta upp till 24 timmar innan det går att söka igen.</div>
                </div>
            </>
        )
        case 'message': return (
            <>
                <div className="active-form">
                    <div className="text-step">Steg 3 av 4</div>
                    <div className="text-title">{content.heading}</div>
                    <div className="validated-values">
                        <span className="checkmark">✓ </span><span >{formValues.email}</span>
                    </div>
                    <div className="form-fields">
                        <div className="price-container">
                            <div className="info-sign">
                                {content.message}
                            </div>
                        </div>
                    </div>
                </div>
            </>
        )
        default: return (<div>Default</div>)
    }

}


export default EstimateForm


const ShowCarValue = ({ price_filtered }) => {

    return (
        <>
            <div className="price-container">
                <div className="field-text">Ungefärligt värde:</div>
                <div className="field-text-estimate">
                    {price_filtered.toLocaleString()} kr
                </div>

                <div className="info-sign">
                    <b>Preliminär onlinevärdering.</b> Vill du gå vidare kommer vi göra en professionell värdering manuellt och återkomma med ett bud på telefon.
                </div>
            </div>
            <div className="text-fineprint">Gå vidare för att få ett dagsaktuellt prisförslag per telefon.</div>
        </>
    )

}

const ShowCarNonValueble = () => {
    return (
        <>
            <div className="price-container">
                <div className="field-text">Ungefärligt värde:</div>
                <div className="field-text-estimate">
                    Kan ej värderas automatiskt
                </div>

                <div className="info-sign warning">
                    Det kan bero på att den är unik på marknaden eller att den är äldre eller gått längre än vårt automatsystem hanterar.
                </div>
            </div>
            <div className="text-fineprint">
                Är du intresserad av att sälja din bil kan du gå vidare för att få en manuell värdering.
            </div>
        </>
    )
}